import { createContext, useContext } from 'react';
import type { ProductTileLookProps } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile';

/**
 * Brand-specific classes for the product listing surfaces (PLP and Algolia PLP): facets, pills, headers, tiles.
 * Defaults are Cava. Panda and Loom inject their own from the product-list tastics.
 */
export type ProductListLook = {
  /** Props forwarded to every product tile in the grid. */
  tile: ProductTileLookProps;
  /** Algolia "load more" button. */
  loadMoreClassName?: string;

  /** Background of facet chips, triggers and range inputs. Loom renders them on the site background. */
  surfaceBgClassName: string;
  /** Background of the mobile facets drawer. */
  drawerBgClassName: string;
  /** Background of the mobile categories drawer. */
  categoriesDrawerBgClassName: string;

  rangeApplyClassName: string;
  mobileClearClassName: string;
  mobileApplyClassName: string;
  mobileCategoriesTitleClassName: string;

  categoryTitleClassName: string;
  facetLabelFontClassName: string;
  pillFontClassName: string;
  searchHeaderFontClassName: string;
};

export const CAVA_PRODUCT_LIST_LOOK: ProductListLook = {
  tile: {},
  surfaceBgClassName: 'bg-white',
  drawerBgClassName: '',
  categoriesDrawerBgClassName: 'bg-white',

  rangeApplyClassName:
    'rounded-sm border-2 border-primary-regular bg-secondary-regular px-14 py-8 font-medium leading-[24px] text-primary-regular transition hover:bg-btn-primary-hover',
  mobileClearClassName:
    'w-full cursor-pointer border-2 border-primary-regular bg-site py-6 text-14 font-medium transition hover:bg-btn-secondary-hover',
  mobileApplyClassName:
    'w-full rounded-md border-2 border-primary-regular bg-secondary-regular py-8 text-14 font-medium text-primary-regular transition hover:bg-btn-primary-hover',
  mobileCategoriesTitleClassName: 'font-secondaryBold text-22',

  categoryTitleClassName: 'font-secondaryBold text-22 leading-[35px] md:text-26 lg:text-52',
  facetLabelFontClassName: 'font-primaryMedium',
  pillFontClassName: 'font-secondaryBold',
  searchHeaderFontClassName: 'font-primaryThin',
};

export const PANDA_PRODUCT_LIST_LOOK: ProductListLook = {
  ...CAVA_PRODUCT_LIST_LOOK,
  tile: {
    imageApi: 'layout',
    imageWrapperClassName: 'bg-white',
    priceRegularClassName: 'text-left text-lg font-medium leading-3',
    priceRegularFontSize: 14,
    priceAsHeading: false,
    priceMedium: true,
    availabilityTagClassNameByKey: {
      'coming-soon': '!text-secondary-regular !border-secondary-regular font-primaryBold',
    },
    compactAvailabilityTagClassName: 'font-primaryThin',
    compactAvailabilityTagClassNameByKey: {
      'coming-soon': '!text-secondary-regular !border-secondary-regular font-primaryBold',
    },
    compactTitleClassName:
      'text-md min-w-0 flex-1 overflow-hidden break-words font-primarySemibold leading-[1.2] line-clamp-2',
  },
  drawerBgClassName: 'bg-white',

  rangeApplyClassName:
    'rounded-sm bg-secondary-regular px-14 py-8 font-medium leading-[24px] text-white transition hover:opacity-90',
  mobileClearClassName: 'w-full cursor-pointer border border-primary-regular py-6 text-14 font-medium',
  mobileApplyClassName:
    'w-full rounded-md bg-secondary-regular py-8 text-14 font-medium text-white transition hover:opacity-90',
  mobileCategoriesTitleClassName: 'font-NunitoSansSemiBold text-22',

  categoryTitleClassName: 'font-secondaryBold text-22 uppercase leading-[35px] md:text-26 lg:text-52',
  facetLabelFontClassName: 'font-MontserratMedium',
  pillFontClassName: 'font-MontserratSemiBold',
  searchHeaderFontClassName: 'font-BarlowSemiCondensedLight',
};

const LOOM_PRIMARY_BUTTON =
  'border border-accent-color bg-accent-color text-neutral-150 outline-offset-0 transition hover:opacity-90 disabled:bg-neutral-300 disabled:border-muted disabled:text-btn-disabled-text disabled:hover:bg-neutral-300 disabled:hover:opacity-100 active:opacity-80 active:shadow-button active:disabled:shadow-none focus-visible:outline focus-visible:outline-accent-color focus-visible:outline-offset-[3px]';

export const LOOM_PRODUCT_LIST_LOOK: ProductListLook = {
  ...CAVA_PRODUCT_LIST_LOOK,
  tile: {
    titleClassName:
      'text-md w-full min-w-0 overflow-hidden break-words line-clamp-2 pt-15 font-primaryBold leading-[1.2] text-black',
    tooltipClassName: 'fixed z-[9999] w-max max-w-240 rounded border border-gray-300 bg-white p-12 text-left shadow-lg',
    priceRegularClassName: 'text-left text-lg font-normal leading-3',
    priceRegularFontSize: 14,
    priceAsHeading: false,
    priceMedium: false,
    availabilityTagClassName: 'font-primaryRegular !text-primary-regular !border-neutral-400',
    availabilityTagClassNameByKey: { 'coming-soon': 'font-primaryBold' },
    compactAvailabilityTagClassName: 'font-primaryRegular !text-primary-regular !border-neutral-400',
    compactAvailabilityTagClassNameByKey: { 'coming-soon': 'font-primaryBold' },
  },
  loadMoreClassName: `mx-auto mt-[90px] block px-48 py-12 text-16 font-medium disabled:opacity-0 ${LOOM_PRIMARY_BUTTON}`,
  surfaceBgClassName: '',
  categoriesDrawerBgClassName: '',

  rangeApplyClassName: `rounded-sm px-14 py-8 font-medium leading-[24px] ${LOOM_PRIMARY_BUTTON}`,
  mobileClearClassName: 'w-full cursor-pointer border border-primary-regular bg-site py-6 text-14 font-medium',
  mobileApplyClassName: `w-full py-8 text-14 font-medium ${LOOM_PRIMARY_BUTTON}`,

  categoryTitleClassName: 'uppercase font-heading text-22 leading-[35px] md:text-26 lg:text-52',
  pillFontClassName: 'font-primaryBold',
  searchHeaderFontClassName: 'font-heading',
};

const ProductListLookContext = createContext<ProductListLook>(CAVA_PRODUCT_LIST_LOOK);

export const ProductListLookProvider = ProductListLookContext.Provider;

export const useProductListLook = () => useContext(ProductListLookContext);
